// <pf-modal titulo="Nueva empresa"><form>...</form></pf-modal>
//
// Envoltorio delgado sobre <dialog> nativo (soporta backdrop, foco atrapado
// y cierre con Escape gratis, sin reimplementar nada de eso a mano). El
// contenido real del modal (formularios, texto) se pasa como children
// normales del elemento — solo el "chrome" (título, botón cerrar, backdrop)
// vive en el Shadow DOM.
//
// API: `abrir()` / `cerrar()`, y el evento `pf-cerrar` cuando el modal se
// cierra (por el botón X, Escape, o click en el backdrop) — quien lo use
// escucha ese evento para, por ejemplo, resetear un formulario. El
// atributo `titulo` es reactivo (observedAttributes/attributeChangedCallback)
// porque el mismo <pf-modal> se reutiliza para alta y edición en varias
// vistas (ej. categorias.js, productos.js) — cambiar el texto del título
// entre "Nueva X" y "Editar: X" sin eso requeriría recrear el elemento.

class Modal extends HTMLElement {
  static get observedAttributes() {
    return ['titulo'];
  }

  connectedCallback() {
    if (this._montado) return;
    this._montado = true;

    const titulo = this.getAttribute('titulo') ?? '';

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        dialog {
          border: none;
          border-radius: 8px;
          padding: 0;
          max-width: min(560px, calc(100vw - 32px));
          width: 100%;
          box-shadow: 0 8px 24px rgba(11, 19, 43, 0.1);
          background: var(--pf-color-superficie, #ffffff);
        }
        dialog::backdrop {
          background: rgba(11, 19, 43, 0.4);
        }
        header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--pf-color-borde, #e2e8f0);
        }
        h2 {
          margin: 0;
          font-family: var(--pf-fuente, 'Inter', system-ui, sans-serif);
          font-size: 1rem;
          font-weight: 600;
          color: var(--pf-color-texto, #0B132B);
        }
        button.cerrar {
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--pf-color-texto-suave, #64748B);
          padding: 6px;
          border-radius: 4px;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        button.cerrar:hover {
          background: var(--pf-color-fondo-alterno, #f8f9fa);
          color: var(--pf-color-texto, #0B132B);
        }
        button.cerrar svg {
          width: 16px;
          height: 16px;
        }
        .cuerpo {
          padding: 20px;
          max-height: 70vh;
          overflow-y: auto;
        }
        footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--pf-espacio-3, 12px);
          padding: 12px 20px;
          border-top: 1px solid var(--pf-color-borde, #e2e8f0);
          background: var(--pf-color-fondo-alterno, #f8f9fa);
        }
      </style>
      <dialog>
        <header>
          <h2>${titulo}</h2>
          <button type="button" class="cerrar" aria-label="Cerrar">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </header>
        <div class="cuerpo">
          <slot></slot>
        </div>
        <footer>
          <slot name="footer"></slot>
        </footer>
      </dialog>
    `;

    this._dialog = this.shadowRoot.querySelector('dialog');
    this.shadowRoot.querySelector('button.cerrar').addEventListener('click', () => this.cerrar());
    // Click en el backdrop (fuera del contenido) cierra — el <dialog> nativo
    // dispara click en sí mismo cuando el click cae fuera de su contenido.
    this._dialog.addEventListener('click', (evento) => {
      if (evento.target === this._dialog) this.cerrar();
    });
    this._dialog.addEventListener('cancel', () => {
      this.dispatchEvent(new CustomEvent('pf-cerrar', { bubbles: true, composed: true }));
    });
  }

  attributeChangedCallback(nombre, valorAnterior, valorNuevo) {
    if (nombre === 'titulo' && this.shadowRoot) {
      const h2 = this.shadowRoot.querySelector('h2');
      if (h2) h2.textContent = valorNuevo ?? '';
    }
  }

  abrir() {
    this._dialog?.showModal();
  }

  cerrar() {
    if (this._dialog?.open) {
      this._dialog.close();
      this.dispatchEvent(new CustomEvent('pf-cerrar', { bubbles: true, composed: true }));
    }
  }
}

customElements.define('pf-modal', Modal);
